import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { ContentCard, LoadingState, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { formatAmount } from "../leadForm";

let draftSeq = 0;
export const draftLot = () => ({
  key: `draft-${++draftSeq}`,
  lotId: null,
  plannedInvoiceDate: "",
  lotValue: "",
  isInvoiced: false,
  dirty: false,
});

/** The lead's Lot 1 is created with it, before it has a date or a value — those read as blank. */
export const lotFromApi = (row) => ({
  key: `lot-${row.lotId}`,
  lotId: row.lotId,
  lotName: row.lotName,
  plannedInvoiceDate: row.plannedInvoiceDate ?? "",
  lotValue: row.lotValue == null ? "" : String(row.lotValue),
  isInvoiced: row.isInvoiced,
  dirty: false,
});

/**
 * How the lead's value is invoiced. Every lead has at least one lot: a lead
 * with none starts with an empty Lot 1. A lot is optional, but once started it needs
 * both its planned invoice date and its value. Saved with the page's Save.
 */
export function InvoiceLotsCard({ lots, loading, editable, currency, balance, errors, onChange, onAdd }) {
  const { t } = useTranslation();
  const total = lots.reduce((sum, lot) => sum + Number(lot.lotValue || 0), 0);
  const columns = "md:grid-cols-[1fr_1.2fr_1.2fr_0.8fr]";

  return (
    <ContentCard
      title={t("leadManagement.lotsTitle")}
      bodyClassName="p-0"
      action={
        editable ? (
          <button type="button" onClick={onAdd} className={ui.filterBtn}>
            <Plus className="h-4 w-4" />
            {t("leadManagement.lotsAdd")}
          </button>
        ) : null
      }
    >
      {loading ? (
        <LoadingState message={t("leadManagement.loading")} />
      ) : (
        <div className="divide-y divide-(--color-text-border)/35">
          <div className={cn(ui.tableHead, "hidden gap-3 px-5 py-3 font-semibold md:grid", columns)}>
            <span>{t("leadManagement.lotsName")}</span>
            <span>{t("leadManagement.lotsPlannedDate")}</span>
            <span>{t("leadManagement.lotsValue")}</span>
            <span>{t("leadManagement.lotsInvoiced")}</span>
          </div>

          {lots.map((lot, index) => (
            <div key={lot.key} className="px-4 py-3 sm:px-5">
              <div className={cn("grid grid-cols-2 items-center gap-3", columns)}>
                <span className="col-span-2 text-sm font-semibold text-foreground md:col-span-1">
                  {lot.lotName || t("leadManagement.lotsLotName", { number: index + 1 })}
                </span>
                <input
                  type="date"
                  aria-label={t("leadManagement.lotsPlannedDate")}
                  value={lot.plannedInvoiceDate}
                  disabled={!editable}
                  onChange={(event) => onChange(lot.key, "plannedInvoiceDate", event.target.value)}
                  className={cn(ui.input, "h-9 w-full disabled:bg-gray-50")}
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  aria-label={t("leadManagement.lotsValue")}
                  value={lot.lotValue}
                  disabled={!editable}
                  onChange={(event) => onChange(lot.key, "lotValue", event.target.value)}
                  className={cn(ui.input, "h-9 w-full disabled:bg-gray-50")}
                />
                <label className="inline-flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={lot.isInvoiced}
                    disabled={!editable}
                    onChange={(event) => onChange(lot.key, "isInvoiced", event.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-primary"
                  />
                  <span className="md:hidden">{t("leadManagement.lotsInvoiced")}</span>
                </label>
              </div>
              {errors[lot.key] ? <p className="mt-1.5 text-[11px] font-semibold text-rose-600">{errors[lot.key]}</p> : null}
            </div>
          ))}

          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 px-5 py-3 text-sm">
            <span>
              <span className="per-file-hint">{t("leadManagement.lotsTotal")}</span>
              <span className="ml-2 font-bold text-foreground">{formatAmount(total, currency)}</span>
            </span>
            <span>
              <span className="per-file-hint">{t("leadManagement.fieldsBalanceAmount")}</span>
              <span className="ml-2 font-bold text-foreground">{formatAmount(balance, currency) || "—"}</span>
            </span>
          </div>
        </div>
      )}
    </ContentCard>
  );
}

export default InvoiceLotsCard;
